import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { ShieldCheck, CircleNotch, Warning, Lock, IdentificationCard } from "@phosphor-icons/react";
import MonoIdentityVerify from "@/components/MonoIdentityVerify";
import { toast } from "sonner";

/**
 * Renders KYC form when status != verified, otherwise the children.
 * NEW UX: parent enters BVN + NIN, clicks Search, and the rest of the form
 * (full_name, dob, phone, gender, address, photo) auto-populates from the
 * Identity API. They review and submit; only last4 of BVN/NIN are persisted.
 */
export default function KYCGate({ children }) {
  const [status, setStatus] = useState("loading");
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState({
    bvn: "", nin: "",
    full_name: "", dob: "", phone: "", gender: "", address: "", photo_url: "",
    bank_account_number: "", bank_code: "",
  });
  const [searched, setSearched] = useState(false);
    const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/parent/kyc/status");
      setStatus(data.status || "not_submitted");
      setRec(data);
    } catch {
      setStatus("not_submitted");
    }
  };
  useEffect(() => { reload(); }, []);

  const onVerified = ({ bvn, nin, identity }) => {
    setForm((f) => ({
      ...f, bvn, nin,
      full_name: identity.full_name || f.full_name,
      dob: identity.dob || f.dob,
      phone: identity.phone || f.phone,
      gender: identity.gender || f.gender,
      address: identity.address || f.address,
      photo_url: identity.photo_url || f.photo_url,
    }));
    setSearched(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!searched) return toast.error("Verify your BVN and NIN first");
    setSubmitting(true);
    try {
      const { data } = await api.post("/parent/kyc/submit", {
        bvn: form.bvn, nin: form.nin,
        full_name: form.full_name, dob: form.dob,
        bank_account_number: form.bank_account_number || undefined,
        bank_code: form.bank_code || undefined,
      });
      setStatus(data.status);
      if (data.status === "verified") toast.success("KYC verified — you can now apply for loans");
      else if (data.status === "failed") toast.error("KYC failed: " + (data.reason || ""));
      else toast.info("KYC under manual review");
      reload();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return <div className="card text-center py-10"><CircleNotch size={28} className="animate-spin mx-auto text-[#0A3622]" /></div>;
  }
  if (status === "verified") return children;

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="kyc-gate">
      <form onSubmit={submit} className="card lg:col-span-7" data-testid="kyc-form">
        <div className="flex items-center gap-2 overline">
          <Lock size={14} weight="bold" /> KYC required
        </div>
        <h2 className="font-display text-2xl font-medium mt-2">Verify your identity to unlock loans</h2>
        <p className="text-sm text-[#4F5D4F] mt-1">Confirm your BVN and NIN on Mono's secure screen, then we'll fill in the rest for you.</p>

        {status === "failed" && (
          <div className="mt-5 p-3 rounded-xl bg-[#FBE3E3] text-[#C93B3B] text-sm flex items-start gap-2" data-testid="kyc-failed-banner">
            <Warning size={16} weight="fill" className="mt-0.5" />
            <span>Last submission failed: {rec?.provider_reason || "Try again with correct details."}</span>
          </div>
        )}
        {status === "manual_review" && (
          <div className="mt-5 p-3 rounded-xl bg-[#FBF1DC] text-[#B45309] text-sm flex items-start gap-2">
            <Warning size={16} weight="fill" className="mt-0.5" />
            <span>Your submission is under manual review. We'll notify you within 24 hours.</span>
          </div>
        )}

        {/* Step 1: BVN one-time code + NIN check (Mono) */}
        <div className="mt-6">
          <MonoIdentityVerify onVerified={onVerified} onReset={() => setSearched(false)} />
        </div>

        {/* Step 2: auto-populated review */}
        {searched && (
          <>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {form.photo_url && (
                <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-xl bg-[#E2F3EB]/40 border border-[#2D7A5D]/20">
                  <img src={form.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white" />
                  <div className="text-sm"><div className="font-medium text-[#0A3622]">Identity photo on file</div><div className="text-xs text-[#4F5D4F]">From the National Identity Database</div></div>
                </div>
              )}
              <Field label="Full legal name" required>
                <input className="input mt-1" required value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="kyc-fullname-input" />
              </Field>
              <Field label="Date of birth" required>
                <input type="date" className="input mt-1" required value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  data-testid="kyc-dob-input" />
              </Field>
              <Field label="Phone number">
                <input className="input mt-1 font-mono" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="kyc-phone-input" />
              </Field>
              <Field label="Gender">
                <input className="input mt-1" value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  data-testid="kyc-gender-input" />
              </Field>
              <Field label="Residential address">
                <input className="input mt-1" value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="kyc-address-input" />
              </Field>
              <Field label="Bank account (optional)">
                <input className="input mt-1 font-mono" maxLength={10}
                  value={form.bank_account_number}
                  onChange={(e) => setForm({ ...form, bank_account_number: e.target.value.replace(/\D/g, "") })}
                  placeholder="0123456789" data-testid="kyc-bank-input" />
              </Field>
              <Field label="Bank code (optional)">
                <input className="input mt-1 font-mono" value={form.bank_code}
                  onChange={(e) => setForm({ ...form, bank_code: e.target.value })}
                  placeholder="058" data-testid="kyc-bankcode-input" />
              </Field>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary mt-7" data-testid="kyc-submit-button">
              {submitting ? "Verifying…" : <>Confirm and verify identity <ShieldCheck size={16} weight="fill" /></>}
            </button>
          </>
        )}
      </form>

      <div className="lg:col-span-5">
        <div className="card !p-7 bg-[#F0EFEA] border border-[#E5E3DB]">
          <div className="overline !text-[#0A3622]">Why we ask for this</div>
          <ul className="mt-4 space-y-3 text-sm text-[#132A13]">
            <li className="flex gap-2"><IdentificationCard size={18} className="text-[#0A3622] mt-0.5 shrink-0" weight="fill" /> <span>CBN regulation: identity must be verified before any credit product.</span></li>
            <li className="flex gap-2"><ShieldCheck size={18} className="text-[#0A3622] mt-0.5 shrink-0" weight="fill" /> <span>Reduces fraud — prevents loan stacking across schools.</span></li>
            <li className="flex gap-2"><ShieldCheck size={18} className="text-[#0A3622] mt-0.5 shrink-0" weight="fill" /> <span>Improves your credit band — verified parents get better APRs.</span></li>
            <li className="flex gap-2"><ShieldCheck size={18} className="text-[#0A3622] mt-0.5 shrink-0" weight="fill" /> <span>One-time. Future loan applications are 1-click.</span></li>
          </ul>
          <div className="mt-6 pt-6 border-t border-[#E5E3DB] text-xs text-[#4F5D4F]">
            We store only the <span className="font-mono text-[#132A13]">last 4 digits</span> of your BVN and NIN. Full numbers are sent encrypted to our identity partner, Mono, and never stored in readable form on our servers.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}{required && <span className="text-[#F2B632]"> *</span>}</span>
      {children}
    </label>
  );
}
